import { fetchWithAuth } from "../lib/api";

// Every status the API's own allowlist accepts (admin_content.py). `qa_failed`
// and `corrupt` are served end to end but were absent from this type, so the UI
// could not name them — which is exactly what the Rejected tab must show.
export type ContentReviewStatus =
  | "attention"
  | "awaiting_review"
  | "revised"
  | "qa_failed"
  | "corrupt"
  | "approved"
  | "parked"
  | "all"
  // PKT-0527 tab keys. A tab is a server-side VIEW over statuses, not a new
  // status: `review` = awaiting_review + revised, `approved` = approved,
  // `rejected` = qa_failed + corrupt + parked.
  | "review"
  | "rejected";

/** The three tabs Grant reviews in, in render order. */
export const CONTENT_REVIEW_TABS = ["review", "approved", "rejected"] as const;
export type ContentReviewTab = (typeof CONTENT_REVIEW_TABS)[number];

// SLICE B adds "H", the terminal REJECT, and nothing else.
// 078 IS APPLIED. This comment previously said the live catalog would refuse an
// "H" until Grant ran the migration by hand; that stopped being true and the
// sentence did not. Evidence is the live catalog, not this file.
// 🔑 Third instance of one stale fact in this repo, found only by grepping for
// the OTHER two. A claim about production duplicated across files goes stale in
// all of them at once and is fixed in one.
export type ContentGridVerdict =
  | "P" | "H" | "K-HOOK" | "K-COPY" | "K-LAYOUT" | "K-PACE" | "K-MIX";

/**
 * PKT-0527 — TERMINAL VERDICTS DISCARD NOTES, BY DESIGN.
 *
 * revisionEngine.ts:36-37 — P: { operations: [], max_attempts: 0,
 * stop_code: "R-APPROVED" }; H: { operations: [], max_attempts: 0,
 * stop_code: "R-HOLD" }. Both are decide-and-stop: zero operations, so a note
 * attached to either can never produce a revision.
 *
 * Measured from production 2026-08-15: the ONE verdict ever written to this
 * system (2026-07-25 12:35:51Z) was verdict "P" carrying review_note "Text on
 * screen needs work" — a CORRECTION filed as an APPROVAL, where the engine
 * discards it. The old interface let one gesture express two intents.
 * These types exist so the compiler refuses to let it happen again.
 *
 * 🔴 SLICE B — WHY "H" IS SUBTRACTED HERE AND NOT JUST ADDED ABOVE.
 *   `ContentRevisionVerdict` used to read `Exclude<ContentGridVerdict, "P">`:
 *   a subtraction of ONE literal, which was the whole allowlist minus the only
 *   terminal that existed. Adding "H" to `ContentGridVerdict` under that
 *   definition would have made "H" silently assignable to `sendBack()` and to
 *   `REVISION_DIALS` — re-opening the exact hole PKT-0527 closed, with no
 *   compiler complaint and no test red. The subtrahend is now the terminal SET,
 *   so a new terminal verdict has to be classified here before it can reach
 *   anything that carries words.
 */
export type ContentApprovalVerdict = "P";
/**
 * Verdicts that END a package: zero operations, zero attempts, a stop code.
 * A terminal is not a dial.
 */
export type ContentTerminalVerdict = ContentApprovalVerdict | "H";
/** Verdicts that queue a re-render. A terminal is never assignable to this. */
export type ContentRevisionVerdict = Exclude<
  ContentGridVerdict,
  ContentTerminalVerdict
>;

/**
 * PKT-0527 SLICE B — 🔴 THE REJECT IS REAL, AND IT IS NOT A DIAL.
 *
 * A real reject is the box verdict "H" (hold, `operations: []`, stop_code
 * R-HOLD), which is what turns a package into status `parked`
 * (LN-Scraper-Workbench reviewCockpit.ts:497) — already inside the Rejected
 * tab's span above, so a rejected reel stays visible as corpus rather than
 * vanishing. The box gates its re-render branch on `verdict.startsWith("K-")`,
 * so an "H" never enqueues a revision, and it is reachable for a package whose
 * `revision_capable` is false — that precondition is scoped to `K-`.
 *
 * ⇒ Wiring the ✗ to a K-* dial would file "this is bad, stop" as "rewrite the
 *   copy" and poison the signal the engine learns taste from. So "H" gets its
 *   own one-tap control, symmetric with Approve — and like Approve it carries
 *   NO note. Words on a zero-operation verdict are the defect PKT-0527 closed;
 *   the reject sheet the first draft of this slice shipped ("say why, so the
 *   engine learns what a bad one looks like") would have re-opened it, because
 *   the engine does nothing with an H's note either.
 */

/**
 * 053:384 — the verdict RPC accepts a package only in these statuses. So the
 * Rejected corpus is HALF actionable: a `qa_failed` reel can still be approved
 * or sent back for a re-render, while `corrupt` and `parked` are read-only
 * history. That is what makes Rejected a corpus to learn from, not a bin.
 */
export const VERDICT_ELIGIBLE_STATUSES = [
  "awaiting_review",
  "revised",
  "qa_failed",
] as const;

/**
 * 🔴 "refused" IS MIGRATION 079: the box understood the verdict and CORRECTLY
 * DECLINED it. Before 079 the ack contract was binary and both branches lied --
 * a refusal acked clean became `applied` ("it worked", on a card where nothing
 * had been made) and a refusal acked with its reason became twenty retries and
 * then `exhausted` ("we tried and failed"), for a command that could never have
 * succeeded.
 *
 * It is TERMINAL: lease_content_review_commands claims only queued/retry and
 * expired leases, so a refused command is never picked up again.
 */
export type ContentCommandState =
  | "queued" | "leased" | "retry" | "applied" | "conflict" | "exhausted"
  | "refused";

export interface ContentCommandTruth {
  child_package_id?: string | null;
  child_ready?: boolean;
  request_id: string;
  /** Manual K-FOOT is read-only command truth here; generic verdict submissions stay unchanged. */
  verdict: ContentGridVerdict | "K-FOOT";
  state: ContentCommandState;
  applied: boolean;
  /** Engine stop_code, e.g. "R-NO-ELIGIBLE-COPY". Only when state==="refused". */
  refusal_code?: string | null;
  /** The engine's own sentence, in full. Only when state==="refused". */
  refusal_reason?: string | null;
}

export interface ContentWorkerHealth {
  state: "online" | "stale" | "offline" | "unknown";
  last_heartbeat_at: string | null;
  oldest_pending_seconds: number | null;
  exhausted_commands: number;
}

export interface ContentSignedMediaLink {
  url: string;
  expires_in_seconds: number;
  sha256: string;
  bytes: number;
}

export interface ContentVerdictResponse extends ContentCommandTruth {
  route?: "box" | "local";
  ok: boolean;
  package_id: string;
  /** The note the store actually retained — not necessarily the one just sent. */
  review_note?: string | null;
}

export const REVIEW_NOTE_MAX = 500;

export interface ContentReviewScene {
  id: string;
  label: string;
  beat_role: string | null;
  caption: string | null;
  clip_id: string | null;
  cue_id: string | null;
  duration_sec: number | null;
  offset_sec: number | null;
}

export interface ContentReviewPostKit {
  caption: string;
  first_comment: string;
  hashtags: string[];
}

export interface ContentReviewItem {
  id: string;
  /** Exact published version. Absent on older APIs: selected-shot editing is unavailable. */
  fingerprint?: string | null;
  reel_id: string;
  subject: string;
  template: string;
  template_version: string;
  status: string;
  verdict: string;
  needs_attention: boolean;
  errors: string[];
  revision_capable: boolean;
  capability_reason: string;
  video_available: boolean;
  frame_sheet_available: boolean;
  /**
   * When the mirror row last changed, ISO-8601. Optional because a response
   * from before this shipped carries none — absent means "we cannot say how
   * old this is", which renders as nothing rather than as "just now".
   *
   * ⚠️ LAST CHANGE, not creation: a re-published card reads as young.
   */
  updated_at?: string;
  /**
   * The building's real name, from migration 107 (PKT-0796).
   *
   * Optional on purpose: a deploy that lands before 107 is applied, or a package
   * the box published without one, simply omits it — and `reelName()` then falls
   * back to the reel-id slug and LABELS that as derived. An absent name must
   * never be rendered as a name.
   */
  property_name?: string;
  property_id?: string;
  command?: ContentCommandTruth | null;
  /*
   * REVIEW CONTEXT (migration 083 · LN-Workbench #523).
   * Optional on the type because a card published before 083 landed carries
   * none of it, and because the box only fills `changed_fields` on a REVISION
   * render — never on a first cut. Treat absent and empty as the same thing.
   */
  /** The reel this one was revised FROM. Chains a version history. */
  parent_reel_id?: string | null;
  /** The real version counter. `template_version` is a hardcoded "v1" and is not one. */
  revision_number?: number;
  /** What the box changed on this revision. Empty on a first render. */
  changed_fields?: string[];
  failed_scene_id?: string | null;
  failed_clip_id?: string | null;
  failed_cue_id?: string | null;
  q_codes?: string[];
  /**
   * PKT-0602 (migration 089) — the reel's beats, so a reviewer can point at one.
   *
   * Optional for the same reason the 083 fields are: a package published before
   * 089, or by a box that has not shipped the scene list yet, carries none. The
   * rollout is box-first, so ABSENT is the normal state until it is not.
   *
   * 🔴 `offset_sec` is the position in the REEL, derived on the box as a
   * cumulative sum. It is NOT the clip's `start_sec`, which is a literal 0.
   * `null` means the scene resolved no clip — it occupied none of the reel,
   * which is not the same as occupying zero seconds of it.
   */
  scenes?: ContentReviewScene[];
  /**
   * PKT-0631 (migration 096) — the post kit the box wrote: caption, first
   * comment, hashtags, as BYTES (a trailing newline is the box's, keep it).
   *
   * `null` is the literal UNKNOWN state and it is the NORMAL state until the box
   * republishes a package after the field shipped: no kit crossed. The sheet
   * must say so, never pre-fill an empty string an operator might post.
   * `undefined` (key absent) means an older API that predates 096 — same UI.
   *
   * 🔴 READ-ONLY (PKT-0631 §C option 1). The brand/fair-housing guards ran on
   * the box. Nothing here edits the kit; the ledger's "caption as posted" is a
   * different field recording what a human actually posted.
   */
  post_kit?: ContentReviewPostKit | null;
  /**
   * PKT-0612 (migration 091) — platforms this reel is already recorded as
   * posted to. Served ONLY on the Approved tab and ONLY when the ledger could
   * be read: ABSENT means unknown (091 not applied, ledger unreadable), an
   * empty array means "read the ledger, nothing recorded". The card must not
   * flatten absent into "not posted".
   */
  posted_platforms?: string[];
}

export interface ContentReviewQueue {
  state: "ready";
  mode: "review_only";
  posting_enabled: false;
  items: ContentReviewItem[];
  counts: Record<string, number | null>;
  counts_complete: boolean;
}

/**
 * PKT-0612 slice 1 — THE POSTED LEDGER. Posting is MANUAL by standing ruling:
 * a human posts in the Instagram app, then records it here from the Approved
 * card. Nothing in this file publishes anything.
 */
export const CONTENT_POST_PLATFORMS = ["instagram", "tiktok", "youtube", "facebook", "other"] as const;
export type ContentPostPlatform = (typeof CONTENT_POST_PLATFORMS)[number];
export const CONTENT_POST_CAPTION_MAX = 2200;

export interface ContentMarkPostedInput {
  platform: ContentPostPlatform;
  /** Optional: the post's https URL, when the human has it. */
  post_url?: string | null;
  /** What was actually posted — the first place the caption exists in the product. */
  caption_as_posted?: string | null;
}

export interface ContentPostRecord {
  ok: boolean;
  id: string;
  package_id: string;
  reel_id: string;
  platform: string;
  posted_at: string;
  /** The session user, written by the server. Never sent from here. */
  posted_by: string;
  caption_as_posted: string;
  post_url: string | null;
  external_media_id: string | null;
  recorded_via: "manual" | "api";
  video_sha256: string | null;
  release_sha: string | null;
  /** true when (reel, platform) was already recorded — one act, one row. */
  replayed: boolean;
}

/**
 * The DoD CONTENT instrument, `content.posted.v1`. `status: "unknown"` with
 * `value: null` when the ledger cannot be read — NEVER a confident 0 — and the
 * header renders that as unknown.
 */
export interface ContentPostedSummary {
  value: number | null;
  status: "ok" | "unknown";
  definition_id: string;
  computed_at: string;
  last_posted_at: string | null;
  per_platform: Record<string, number> | null;
  note?: string;
}

const mediaPath = (itemId: string, kind: "video" | "frame-sheet") =>
  `/admin/content/reviews/${encodeURIComponent(itemId)}/media/${kind}`;

/** A filesystem-safe .mp4 name Grant can find in his Downloads folder. */
export const downloadFilename = (item: {
  reel_id?: string;
  id: string;
  subject?: string;
}): string => {
  const stem = `${item.subject || item.reel_id || item.id}`
    .normalize("NFKD")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    || "reel";
  return `${stem}.mp4`;
};

// Standalone so `download` can call it without referencing the service object
// from inside its own initializer (which TypeScript cannot type).
const fetchMediaLink = (itemId: string, kind: "video" | "frame-sheet") =>
  fetchWithAuth<ContentSignedMediaLink>(mediaPath(itemId, kind));

export const contentReviewService = {
  list: (status: ContentReviewStatus, limit = 24) =>
    fetchWithAuth<ContentReviewQueue>(
      `/admin/content/reviews?status=${encodeURIComponent(status)}&limit=${limit}`
    ),

  media: fetchMediaLink,

  health: () => fetchWithAuth<ContentWorkerHealth>("/admin/content/health"),

  /** PKT-0612 — record a manual post. The body can never name posted_by. */
  markPosted: (itemId: string, input: ContentMarkPostedInput) =>
    fetchWithAuth<ContentPostRecord>(
      `/admin/content/reviews/${encodeURIComponent(itemId)}/posted`,
      {
        method: "POST",
        body: JSON.stringify({
          platform: input.platform,
          post_url: input.post_url?.trim() ? input.post_url.trim() : null,
          caption_as_posted: input.caption_as_posted?.trim()
            ? input.caption_as_posted.trim().slice(0, CONTENT_POST_CAPTION_MAX)
            : null,
        }),
      }
    ),

  /** PKT-0612 — the ledger count, or an honest unknown. */
  postedSummary: () =>
    fetchWithAuth<ContentPostedSummary>("/admin/content/posted-summary"),

  verdict: (
    itemId: string,
    verdict: ContentGridVerdict,
    requestId: string,
    reviewNote?: string | null
  ) =>
    fetchWithAuth<ContentVerdictResponse>(
      "/admin/content/reviews/verdict",
      {
        method: "POST",
        body: JSON.stringify({
          package_id: itemId,
          verdict,
          request_id: requestId,
          review_note: reviewNote?.trim() ? reviewNote.trim().slice(0, REVIEW_NOTE_MAX) : null,
        }),
      }
    ),

  /**
   * PKT-0527 — the payoff. Grant approves a reel, then takes the file and
   * posts it. The signed URL lives 60s and is cross-origin, so `<a download>`
   * on it would be ignored by the browser and merely navigate away from the
   * app. Reading it into a blob first is what actually saves a file.
   */
  download: async (item: { id: string; reel_id?: string; subject?: string }) => {
    const link = await contentReviewService.media(item.id, "video");
    const response = await fetch(link.url);
    if (!response.ok) throw new Error("CONTENT_DOWNLOAD_FAILED");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = downloadFilename(item);
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      // Revoking in the same tick can abort the save in some browsers.
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    }
    return { bytes: link.bytes, sha256: link.sha256 };
  },
};
